"use client";

import { useAuth } from "../providers/AuthProvider";
import { useTheme } from "../providers/ThemeProvider";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";

export function ProfileDropdown() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });

  const displayName = user?.displayName ?? user?.phone ?? "User";
  const initial = displayName.charAt(0).toUpperCase();

  // Close on click/tap outside — check both button and portal menu
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        btnRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(!open);
  };

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: menuPos.top,
          right: menuPos.right,
          background: "var(--color-bg-input)",
          border: "1px solid var(--color-border-secondary)",
          borderRadius: 6,
          minWidth: 180,
          zIndex: 9999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "0.6rem 0.75rem",
            fontSize: "0.75rem",
            color: "var(--color-text-muted)",
            borderBottom: "1px solid var(--color-border-secondary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </div>
        <Link
          href="/dashboard"
          onClick={() => setOpen(false)}
          style={menuItemStyle}
        >
          Dashboard
        </Link>
        <Link
          href="/about"
          onClick={() => setOpen(false)}
          style={menuItemStyle}
        >
          About Occum
        </Link>
        <button
          onClick={() => {
            toggleTheme();
          }}
          style={{
            ...menuItemStyle,
            width: "100%",
            textAlign: "left",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {theme === "light" ? "Dark Mode" : "Light Mode"}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            logout();
          }}
          style={{
            ...menuItemStyle,
            width: "100%",
            textAlign: "left",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            borderTop: "1px solid var(--color-border-secondary)",
          }}
        >
          Sign Out
        </button>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          color: "var(--color-text-muted)",
          fontSize: "1.1rem",
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 44,
        }}
        title={displayName}
      >
        &#x22ee;
      </button>
      {menu}
    </>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: "block",
  padding: "0.5rem 0.75rem",
  fontSize: "0.8rem",
  color: "var(--color-text)",
  textDecoration: "none",
};
